import React, { useMemo } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { shareInstagramStories } from '#src/app/shared/share/instagramStories';

import type { ShareActionComponentPropsWithOptions } from '../types';

import { useEducationModal } from './useEducationModal';

export const InstagramStoriesShare: React.FC<ShareActionComponentPropsWithOptions> = ({
	children,
	options,
	onAfterAction,
	onBeforeAction,
}) => {
	/**
	 * useEducationModal handles context detection internally:
	 * - returns [sharedOpenFn, null] when ShareEducationModalProvider is in the tree
	 * - returns [localOpenFn, <Modal>] otherwise (standalone fallback)
	 */
	const [openEducationModal, educationModal] = useEducationModal();
	const track = useTracking();
	const utilityContext = useUtilityContext();

	const onClick = useMemo(() => {
		return () => {
			void (async () => {
				await onBeforeAction?.();

				const {
					url,
					content: { image },
					trackingData,
					trackingEventName,
					config: { instagramStories },
				} = options;

				const success = await shareInstagramStories(
					{ url, image },
					{
						trackingData,
						picker: instagramStories?.picker,
						trackingEvents: {
							success: trackingEventName,
							intent: 'instagram_stories_share_story_modal_view',
							continue: 'instagram_stories_share_story_modal_continue',
							cancel: 'instagram_stories_share_story_modal_close',
							failure: `${trackingEventName}_failure`,
						},
						// Spread rather than destructured: picking keys here once silently dropped `onAssetSelected`
						// and killed the picker's tracking event. The layout is added on rather than threaded
						// through `shareInstagramStories`, which has no business knowing which picker renders.
						openStoriesEducationModal: (modalOptions) =>
							openEducationModal({ ...modalOptions, pickerLayout: instagramStories?.picker?.layout }),
					},
					track,
					utilityContext,
				);

				await onAfterAction?.(success);
			})();
		};
	}, [onBeforeAction, onAfterAction, options, openEducationModal, track, utilityContext]);

	return (
		<>
			{React.cloneElement(children, {
				...children.props,
				onClick,
			})}
			{educationModal}
		</>
	);
};
